/**
 * <tvpage-carousel> — lazily loads a TVPage (Creatable) carousel widget when it
 * scrolls into view. Self-initializing custom element.
 *
 * Attributes:
 *   data-widget-id   e.g. "w41391"            (global key + script id)
 *   data-entry-point e.g. "https://cdn.tvpagewidgets.com/v3/1759753/41391.js"
 *   data-target      e.g. "w41391-el"         (id of the inner mount div)
 *   data-client-id   e.g. "1759753"
 *   data-client-hash e.g. "49a55190"
 *   data-product-sku optional, defaults to ""
 */
(function () {
  if (customElements.get('tvpage-carousel')) return;

  const OBSERVER_THRESHOLD = 0;
  // Start loading well before the section scrolls into view so the widget is
  // ready by the time the user reaches it (prevents the late-fill layout jump).
  const OBSERVER_ROOT_MARGIN = '1200px 0px';

  class TvpageCarousel extends HTMLElement {
    connectedCallback() {
      if (this._observer || this._loaded) return;

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries, obs) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                this.loadWidget();
                obs.unobserve(this);
              }
            }
          },
          { threshold: OBSERVER_THRESHOLD, rootMargin: OBSERVER_ROOT_MARGIN }
        );
        this._observer.observe(this);
      } else {
        // Fallback: load immediately.
        this.loadWidget();
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._observer = null;
    }

    loadWidget() {
      const widgetId = this.dataset.widgetId;
      const entryPoint = this.dataset.entryPoint;
      const target = this.dataset.target;

      if (!widgetId || !entryPoint || !target) {
        console.warn('[tvpage-carousel] Missing required data attributes — load skipped.');
        return;
      }

      // Guard against double-load if the same widget exists elsewhere on the page.
      if (window[`__tvpageLoaded_${widgetId}`]) return;
      window[`__tvpageLoaded_${widgetId}`] = true;

      // Creatable bootstrap (third-party). Keeps the global queue + script id contract.
      (function (w, d, s, o, f, t) {
        w['Creatable'] = w.Creatable || {};
        w.Creatable[o] = { type: t };
        w[o] = w[o] || function () { (w[o].q = w[o].q || []).push(arguments); };
        const js = d.createElement(s);
        const fjs = d.getElementsByTagName(s)[0];
        js.id = o;
        js.src = f;
        js.async = true;
        fjs.parentNode.insertBefore(js, fjs);
      })(window, document, 'script', widgetId, entryPoint, 'carousel');

      window[widgetId]('init', {
        targetElement: target,
        productSKU: this.dataset.productSku || '',
        clientId: Number(this.dataset.clientId),
        clientHash: this.dataset.clientHash,
      });
    }
  }

  customElements.define('tvpage-carousel', TvpageCarousel);
})();